import React, {useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {CleanupService} from "../../service/CleanupService";
import {ProductService} from "../../service/ProductService";
import {CampaignService} from "../../service/CampaignService";
import {getHostName, isAdmin} from "../../utils/Utils";

const cleanupService = new CleanupService();
const productService = new ProductService();
const campaignService = new CampaignService();

const DUP_FIELDS = [
    {name: 'SKU', code: 'sku'}, {name: 'MPN', code: 'mpn'}, {name: 'EAN', code: 'ean'},
    {name: 'UPC', code: 'upc'}, {name: 'Barcode', code: 'barcode'}, {name: 'ASIN', code: 'asin'},
    {name: 'Ref ID', code: 'ref_id'},
];

// Each cleanup action as a card with a description + confirmation.
// run(campaignId, ctx) -> promise. danger flags destructive (red) actions.
const ACTIONS = [
    {group: 'products', key: 'remove_duplicate_products', labelKey: 'clean_up.remove_duplicate_products', icon: 'pi pi-clone', danger: true, needsField: true,
        run: (id, c) => cleanupService.deleteDuplicateProducts(id, c.field)},
    {group: 'products', key: 'inactivate_duplicate_products', labelKey: 'clean_up.inactivate_duplicate_products', icon: 'pi pi-ban', danger: true, needsField: true,
        run: (id, c) => cleanupService.inactivateDuplicateProducts(id, c.field)},
    {group: 'products', key: 'deactivate_products_with_no_longer_available_owner', labelKey: 'clean_up.deactivate_products_with_no_longer_available_owner', icon: 'pi pi-ban', danger: true, needsDays: true,
        run: (id, c) => cleanupService.deactivateProductsWithNoLongerAvailableOwner(id, c.days)},
    {group: 'products', key: 'refresh_image_urls', labelKey: 'actions.refresh_image_urls', icon: 'pi pi-images', danger: false,
        run: (id) => productService.refreshImageUrls(id)},
    {group: 'products', key: 'clear_product_images', labelKey: 'clean_up.clear_product_images', icon: 'pi pi-image', danger: true,
        run: (id) => cleanupService.clearProductImages(id)},
    {group: 'products', key: 'refresh_product_names', labelKey: 'actions.refresh_product_names', icon: 'pi pi-refresh', danger: false,
        run: (id) => productService.refreshProductNames(id)},
    {group: 'sellers', key: 'remove_duplicate_sellers', labelKey: 'clean_up.remove_duplicate_sellers', icon: 'pi pi-sitemap', danger: true,
        run: (id) => cleanupService.deleteDuplicateSellers(id)},
    {group: 'sellers', key: 'remove_duplicate_sellers_by_name', labelKey: 'clean_up.remove_duplicate_sellers_by_name', icon: 'pi pi-sitemap', danger: true,
        run: (id) => cleanupService.deleteDuplicateSellersByName(id)},
    {group: 'suggestions', key: 'remove_duplicate_suggestions', labelKey: 'clean_up.remove_duplicate_suggestions', icon: 'pi pi-sitemap', danger: true,
        run: (id) => cleanupService.deleteDuplicateSuggestions(id)},
    {group: 'suggestions', key: 'set_suggestions_unmatched', labelKey: 'clean_up.set_suggestions_unmatched', icon: 'pi pi-undo', danger: true,
        run: (id) => cleanupService.setSuggestionsUnmatched(id)},
    {group: 'suggestions', key: 'clear_competitor_suggestions', labelKey: 'actions.clear_competitor_suggestions', icon: 'pi pi-google', danger: true,
        run: (id) => cleanupService.clearCompetitorSuggestions(id)},
    {group: 'suggestions', key: 'clear_competitor_suggestions_used_base_spider', labelKey: 'actions.clear_competitor_suggestions_used_base_spider', icon: 'pi pi-google', danger: true, adminOnly: true,
        run: (id) => cleanupService.clearCompetitorSuggestionsUsedBasedSpider(id)},
    {group: 'suggestions', key: 'clear_new_competitor_suggestions', labelKey: 'actions.clear_new_competitor_suggestions', icon: 'pi pi-google', danger: true,
        run: (id) => cleanupService.clearNewCompetitorSuggestions(id)},
    {group: 'mapViolation', key: 'clear_violation_strategies', labelKey: 'actions.clear_violation_strategies', icon: 'pi pi-shield', danger: true, adminOnly: true,
        run: (id) => cleanupService.clearViolationStrategies(id)},
    {group: 'mapViolation', key: 'remove_ismap_ismrp_from_competitors', labelKey: 'actions.remove_ismap_ismrp_from_competitors', icon: 'pi pi-shield', danger: true, adminOnly: true,
        run: (id) => cleanupService.removeIsMapIsMrpFromCompetitors(id)},
];

const GROUPS = ['products', 'sellers', 'suggestions', 'mapViolation'];

const CleanUpOnboardingStep = ({t}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    const [field, setField] = useState('sku');
    const [days, setDays] = useState(100);
    const [confirm, setConfirm] = useState(null); // the action pending confirmation
    const [running, setRunning] = useState(false);

    // Low-score cleanup dialog state
    const [showLowScore, setShowLowScore] = useState(false);
    const [minScore, setMinScore] = useState(70);
    const [selectedDomains, setSelectedDomains] = useState([]);
    const [domainOptions, setDomainOptions] = useState([]);
    const [previewCount, setPreviewCount] = useState(null);
    const [previewAffected, setPreviewAffected] = useState([]);
    const [lowScoreBusy, setLowScoreBusy] = useState(false);

    // Deep link to a product's Suggestions tab, filtered to only the affected rows.
    const affectedLink = (p) =>
        "/#/productdetail?id=" + p.id + "&tab=suggestions&affected=" +
        encodeURIComponent(btoa(JSON.stringify(p.suggestion_urls || [])));

    const execute = () => {
        const action = confirm;
        setRunning(true);
        action.run(campaignId, {field, days}).then(() => {
            toast.current.show({severity: 'success', summary: t(action.labelKey), detail: t('message_detail.successful')});
            setConfirm(null);
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setRunning(false));
    };

    const openLowScoreDialog = () => {
        // Reset form state for a fresh dialog
        setMinScore(70);
        setSelectedDomains([]);
        setDomainOptions([]);
        setPreviewCount(null); setPreviewAffected([]);
        setShowLowScore(true);
        campaignService.getCampaignCompetitors(campaignId, false).then(competitors => {
            const seen = new Set();
            const opts = [];
            (competitors || []).forEach(c => {
                const host = getHostName(c.url);
                if (host && !seen.has(host)) {
                    seen.add(host);
                    opts.push({label: host, value: host});
                }
            });
            setDomainOptions(opts);
        }).catch(e => {
            setError(e);
            setError(null);
        });
    };

    const runLowScorePreview = () => {
        setLowScoreBusy(true);
        cleanupService.cleanupLowScoreMatches(campaignId, minScore, selectedDomains, true).then(data => {
            setPreviewCount(data.count);
            setPreviewAffected(data.affected_products || []);
        }).catch(e => {
            setError(e);
            setError(null);
        }).finally(() => setLowScoreBusy(false));
    };

    const runLowScoreConfirm = () => {
        setLowScoreBusy(true);
        cleanupService.cleanupLowScoreMatches(campaignId, minScore, selectedDomains, false).then(() => {
            toast.current.show({
                severity: 'success',
                summary: t('clean_up.cleanup_low_score_matches'),
                detail: t('message_detail.successful'),
            });
            setShowLowScore(false);
            setPreviewCount(null); setPreviewAffected([]);
            setMinScore(70);
            setSelectedDomains([]);
        }).catch(e => {
            setError(e);
            setError(null);
        }).finally(() => setLowScoreBusy(false));
    };

    const renderCard = (a) => (
        <div key={a.key} style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff',
            display: 'flex', flexDirection: 'column', gap: 8}}>
            <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                <i className={a.icon} style={{color: a.danger ? '#dc2626' : '#16a34a'}}/> {t(a.labelKey)}
            </div>
            <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.cleanup.desc.' + a.key)}</small>
            {a.needsField && (
                <Dropdown value={field} options={DUP_FIELDS} optionLabel="name" optionValue="code"
                          style={{width: '100%'}} onChange={(e) => setField(e.value)}/>
            )}
            {a.needsDays && (
                <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <InputNumber value={days} min={0} showButtons inputStyle={{width: 80}} onValueChange={(e) => setDays(e.value)}/>
                    <small style={{color: '#6b7280'}}>{t('date.days', 'gün')}</small>
                </span>
            )}
            <Button label={t('onboarding.cleanup.run')} icon={a.icon}
                    className={a.danger ? 'p-button-outlined p-button-danger' : 'p-button-outlined p-button-success'}
                    onClick={() => setConfirm(a)}/>
        </div>
    );

    return (
        <div>
            <Message severity="warn" text={t('onboarding.cleanup.warning')} style={{display: 'block', marginBottom: 12}}/>

            {GROUPS.map(g => {
                const items = ACTIONS.filter(a => a.group === g && (!a.adminOnly || isAdmin()));
                const showLowScoreCard = g === 'suggestions' && isAdmin();
                if (items.length === 0 && !showLowScoreCard) return null;
                return (
                    <div key={g} style={{marginBottom: 18}}>
                        <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.cleanup.grp.' + g)}</div>
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14}}>
                            {items.map(renderCard)}
                            {showLowScoreCard && (
                                <div style={{border: '1px solid #fca5a5', borderRadius: 10, padding: 16, background: '#fff',
                                    display: 'flex', flexDirection: 'column', gap: 8}}>
                                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                                        <i className="pi pi-trash" style={{color: '#dc2626'}}/> {t('clean_up.cleanup_low_score_matches')}
                                    </div>
                                    <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.cleanup.desc.cleanup_low_score_matches')}</small>
                                    <Button label={t('onboarding.cleanup.run')} icon="pi pi-trash"
                                            className="p-button-outlined p-button-danger"
                                            onClick={openLowScoreDialog}/>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            <Dialog header={confirm ? t(confirm.labelKey) : ''} visible={!!confirm}
                    style={{width: '460px', maxWidth: '95vw'}} modal onHide={() => !running && setConfirm(null)}
                    footer={confirm &&
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text" disabled={running} onClick={() => setConfirm(null)}/>
                            <Button label={t('onboarding.cleanup.run')} icon="pi pi-check"
                                    className={confirm.danger ? 'p-button-danger' : ''}
                                    loading={running} onClick={execute}/>
                        </div>}>
                {confirm && <div>
                    <p style={{marginTop: 0}}>{t('onboarding.cleanup.desc.' + confirm.key)}</p>
                    {confirm.danger && <Message severity="warn" text={t('onboarding.cleanup.irreversible')} style={{display: 'block'}}/>}
                </div>}
            </Dialog>

            {/* Low-score matches cleanup: preview then confirm. */}
            <Dialog header={t('clean_up.cleanup_low_score_matches')} visible={showLowScore}
                    style={{width: '520px', maxWidth: '95vw'}} modal
                    onHide={() => {
                        if (!lowScoreBusy) {
                            setPreviewCount(null); setPreviewAffected([]);
                            setMinScore(70);
                            setSelectedDomains([]);
                            setShowLowScore(false);
                        }
                    }}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={lowScoreBusy} onClick={() => setShowLowScore(false)}/>
                            {previewCount === null
                                ? <Button label={t('onboarding.cleanup.lowScore.preview')} icon="pi pi-search"
                                          loading={lowScoreBusy} disabled={lowScoreBusy}
                                          onClick={runLowScorePreview}/>
                                : <Button label={t('onboarding.cleanup.lowScore.confirm')} icon="pi pi-trash"
                                          className="p-button-danger"
                                          loading={lowScoreBusy} disabled={lowScoreBusy}
                                          onClick={runLowScoreConfirm}/>}
                        </div>
                    }>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    <div>
                        <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>
                            {t('onboarding.cleanup.lowScore.minScore')}
                        </label>
                        <InputNumber value={minScore} min={0} max={100} showButtons
                                     inputStyle={{width: 80}}
                                     onValueChange={(e) => { setMinScore(e.value); setPreviewCount(null); setPreviewAffected([]); }}/>
                    </div>
                    <div>
                        <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>
                            {t('onboarding.cleanup.lowScore.domains')}
                        </label>
                        <MultiSelect value={selectedDomains} options={domainOptions}
                                     display="chip" filter
                                     placeholder={t('onboarding.cleanup.lowScore.domains')}
                                     style={{width: '100%'}}
                                     onChange={(e) => { setSelectedDomains(e.value || []); setPreviewCount(null); setPreviewAffected([]); }}/>
                    </div>
                    {previewCount !== null && (
                        <Message severity="warn"
                                 text={t('onboarding.cleanup.lowScore.previewCount', {count: previewCount})}
                                 style={{display: 'block'}}/>
                    )}
                    {previewCount !== null && previewAffected.length > 0 && (
                        <div>
                            <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>
                                {t('onboarding.affectedProducts')}
                            </label>
                            <div style={{maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb',
                                borderRadius: 8, padding: 8}}>
                                {previewAffected.map(p => (
                                    <div key={p.id} style={{display: 'flex', justifyContent: 'space-between',
                                        gap: 8, padding: '3px 0'}}>
                                        <a href={affectedLink(p)} target="_blank" rel="noopener noreferrer"
                                           style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                            {p.name || p.id}
                                        </a>
                                        <span style={{color: '#6b7280', flexShrink: 0}}>{(p.suggestion_urls || []).length}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default CleanUpOnboardingStep;
